import React from 'react';
import {
  ACTION_LABELS,
  ActionRequest,
  ActionResolution,
  executeActionRequest,
} from '../../../services/trips/actionRequests';
import ActionResolutionView from './ActionResolutionView';
import './chatCards.css';

interface ActionRequestCardProps {
  request: ActionRequest;
  resolution?: ActionResolution;
  createdTripId?: string;
  resolutionNote?: string;
  backTo?: string;
  conversationId?: string;
  // UUID of the trip already created for this chat. When set, a CREATE_TRIP
  // proposal is blocked (a chat gets at most one trip).
  existingTripId?: string;
  fallbackActivities?: Array<Record<string, any>>;
  onResolve: (resolution: ActionResolution, note: string, tripId?: string) => void;
  /**
   * Shared trip chat only: claim before applying — the other participant sees
   * this same card, and only the first confirm may apply it (CD-5). `won: false`
   * turns the card into a receipt and applies nothing.
   */
  claim?: () => Promise<{ won: boolean; note?: string }>;
}

const summarize = (request: ActionRequest): string | null => {
  const payload = request.payload || {};
  switch (request.action) {
    case 'ADD_ACTIVITIES': {
      const names = (payload.activities || [])
        .map((activity: any) => activity.name || activity.title)
        .filter(Boolean);
      return names.length ? names.join(' · ') : null;
    }
    case 'ADD_CHECKLIST_ITEMS': {
      // Named, not counted — you are confirming a write, so you should be able
      // to read exactly what it will add.
      const titles = (payload.items || [])
        .map((item: any) => item?.title || item?.name)
        .filter(Boolean);
      return titles.length ? titles.join(' · ') : null;
    }
    case 'UPDATE_ACTIVITY':
      return payload.name || null;
    case 'UPDATE_TRIP':
      return Object.keys(payload).filter((key) => key !== 'activity_id').join(', ') || null;
    default:
      return null;
  }
};

const ActionRequestCard: React.FC<ActionRequestCardProps> = ({
  request,
  resolution,
  createdTripId,
  resolutionNote,
  backTo = '/ask-ai',
  conversationId,
  existingTripId,
  fallbackActivities,
  onResolve,
  claim,
}) => {
  const [busy, setBusy] = React.useState(false);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (claim) {
        const outcome = await claim();
        if (!outcome.won) {
          // Someone else confirmed it first, or the claim could not be made.
          // Applying anyway is exactly the double-apply this guards against.
          onResolve(outcome.note?.startsWith('Confirmed') ? 'confirmed' : 'failed',
            outcome.note || 'Could not confirm this — try again.');
          return;
        }
      }
      const result = await executeActionRequest(request, { conversationId, existingTripId, fallbackActivities });
      onResolve('confirmed', result.note, result.tripId);
    } catch (error: any) {
      onResolve('failed', error?.message || 'The change could not be applied');
    } finally {
      setBusy(false);
    }
  };

  if (resolution) {
    return (
      <ActionResolutionView
        resolution={resolution}
        note={resolutionNote}
        tripId={createdTripId}
        backTo={backTo}
      />
    );
  }

  // A chat gets at most one trip: if one already exists for this conversation,
  // block the CREATE_TRIP proposal and point at the existing trip instead.
  if (request.action === 'CREATE_TRIP' && existingTripId) {
    return (
      <ActionResolutionView
        resolution="confirmed"
        note="This chat already has a trip"
        tripId={existingTripId}
        backTo={backTo}
      />
    );
  }

  const summary = summarize(request);

  return (
    <div className="v2-chat-card" role="group" aria-label="Confirm suggested change">
      <p className="v2-chat-card__title">{ACTION_LABELS[request.action] || request.action}?</p>
      {summary && <p className="v2-chat-card__summary">{summary}</p>}
      <div className="v2-chat-card__actions">
        <button type="button" className="v2-chat-card__confirm" onClick={handleConfirm} disabled={busy}>
          {busy ? 'Applying…' : 'Confirm'}
        </button>
        <button
          type="button"
          className="v2-chat-card__cancel"
          onClick={() => onResolve('dismissed', 'Dismissed')}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ActionRequestCard;
